import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Services from "../../services/services";
import { toast } from "react-toastify";
import { setProfileShowDeposit } from "../../reducers/authSlice";
import { CircleCheck, CircleDot, CircleX } from "lucide-react";
import CreateDepositPopup from "./CreateDepositPopup";
import DepositInfoPopup from "./DepositInfoPopup";
import Title from "../../components/Title";
import { fetchUserData, RoutePages } from "../../ultils";
import InvoicePopup from "../../components/InvoicePopup";
import { useNavigate } from "react-router-dom";


const Profile = () => {
    const authStore = useSelector((state) => state.auth);
    const isShowDepositPopup = useSelector((state) => state.auth.isShowDepositPopup);
    const setIsShowDepositPopup = (value) => dispatch(setProfileShowDeposit(value));

    const [verifyLoading, setVerifyLoading] = useState(false);
    const [verifyCode, setVerifyCode] = useState(null);
    const [dataDeposits, setDataDeposits] = useState([]);

    const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmNewPassword, setConfirmNewPassword] = useState("");

    const [depositPaymentPopup, setDepositPaymentPopup] = useState(null);
    const [invoicePopup, setInvoicePopup] = useState(null);

    const dispatch = useDispatch();
    const navigate = useNavigate();


    const fetchDeposits = async () => {
        const res = await Services.Deposit.getDeposits();
        const data = await res.json();
        if (res.status === 200 && data.success === true) {
            setDataDeposits(data.data || []);
        }
        else {
            toast.error(`${res.status}: ${data.error ?? ""}`)
        }
    };


    // Fetching data
    useEffect(() => {
        fetchDeposits();
    }, []);

    const handleSendCode = async () => {
        setVerifyLoading(true);
        try {
            const res = await Services.UserAuth.sendVerifyCode();
            const data = await res.json();
            if (res.status === 200 && data.success === true)
                toast.success(data.message);
            else
                toast.error(data.error ?? "An error occurred while sending the code.");
        }
        finally {
            setVerifyLoading(false);
        }
    };
    const handleVerifyCode = async () => {
        setVerifyLoading(true);
        try {
            const res = await Services.UserAuth.verifyCode(verifyCode);
            const data = await res.json();
            if (res.status === 200 && data.success === true) {
                toast.success(data.message);
                fetchUserData(dispatch, navigate);
            }
            else
                toast.error(data.error ?? "An error occurred while sending the code.");
        }
        finally {
            setVerifyLoading(false);
        }
    }

    const handleCancelDeposit = async (deposit) => {
        const res = await Services.Deposit.cancel(deposit.id);
        const data = await res.json();
        if (res.ok && data.success) {
            fetchDeposits()
            toast.success("Deposit canceled!")
        } else {
            toast.error(`${res.status}: ${data.error ?? ""}`)
        }
    }

    const handleInvoice = (deposit) => {
        setInvoicePopup(deposit);
    }

    return (
        <div className="w-full">
            <Title page={RoutePages.USER} title="User" />
            <div className="flex gap-5 p-2 w-full flex-col sm:flex-row">
                {/* Responsive info section */}
                <div className="flex flex-col w-full md:w-fit items-start gap-3">
                    <h2 className="text-2xl font-bold mt-4">Personal Information</h2>
                    <div className="flex flex-col w-full border-2 rounded-md p-2 gap-3 bg-white shadow">
                        <div className="flex gap-3">
                            <span className="font-bold">User ID:</span>
                            <span className="font-medium">{authStore.user?.id}</span>
                        </div>
                        <div className="flex gap-3">
                            <span className="font-bold">Username:</span>
                            <span className="font-medium">{authStore.user?.username}</span>
                        </div>
                        <div className="flex gap-3">
                            <span className="font-bold">Display name:</span>
                            <span className="font-medium">{authStore.user?.display_name}</span>
                        </div>
                        <div className="flex gap-3">
                            <label className="font-bold">Created:</label>
                            <label className="font-medium">{new Date(authStore.user?.created_at).toLocaleString()}</label>
                        </div>
                        <div className="flex gap-3 items-center">
                            <label className="font-bold">Balance:</label>
                            <span className="bg-gray-300 hover:bg-green-500 cursor-pointer px-3 py-1 rounded" onClick={() => setIsShowDepositPopup(true)}>
                                ${authStore.user?.balance?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                        </div>
                    </div>
                    <div className={`flex flex-col gap-2 w-full border-2 rounded-md p-2 ${verifyLoading ? "opacity-50 pointer-events-none" : ""}`}>
                        <div className="flex w-fit gap-3 items-center">
                            <label className="font-bold">Email:</label>
                            <label className="font-medium break-all">{authStore.user?.email}</label>
                        </div>
                        {authStore.user?.is_verified
                            ? <span className="block text-green-700 italic">Your email have been verified!</span>
                            : <div className="flex flex-col justify-start gap-3">
                                <span className="block text-red-500 italic">Please verify your Email!</span>
                                <div className="flex flex-col sm:flex-row gap-2">
                                    <button onClick={() => handleSendCode()} className="w-full sm:w-24 h-8 bg-blue-500 text-white rounded hover:bg-blue-600">
                                        1-Get code
                                    </button>
                                    <input className="w-full sm:w-32 px-3 py-1 border" placeholder="2-Put it here..." type="text" onChange={(e) => setVerifyCode(e.target.value)}>
                                    </input>
                                    <button onClick={() => handleVerifyCode()} className="w-full sm:w-20 h-8 bg-blue-500 text-white rounded hover:bg-blue-600">
                                        3-Verify
                                    </button>
                                </div>
                            </div >
                        }
                    </div>
                    <div className="w-full border-2 rounded-md p-2 bg-white shadow">
                        <h3 className="text-lg font-bold">Reset Password</h3>
                        <form className="space-y-3" onSubmit={async (e) => {
                            e.preventDefault();
                            setResetPasswordLoading(true);
                            try {
                                const res = await Services.UserAuth.resetPassword(currentPassword, newPassword, confirmNewPassword);
                                const data = await res.json();
                                if (!res.ok || !data.success) {
                                    toast.error(data.error || "Error resetting password.");
                                } else {
                                    toast.success(data.message || "Password updated successfully.");
                                    setCurrentPassword("");
                                    setNewPassword("");
                                    setConfirmNewPassword("");
                                }
                            } catch (err) {
                                toast.error("Network error. Please try again.");
                            }
                            setResetPasswordLoading(false);
                        }}
                        >
                            <div>
                                <label className="block font-medium">Current Password:</label>
                                <input
                                    type="password"
                                    value={currentPassword}
                                    onChange={e => setCurrentPassword(e.target.value)}
                                    required
                                    className="w-full border border-gray-300 rounded px-3 py-1 focus:outline-none focus:ring focus:ring-blue-200"
                                />
                            </div>
                            <div>
                                <label className="block font-medium">New Password:</label>
                                <input
                                    type="password"
                                    value={newPassword}
                                    onChange={e => setNewPassword(e.target.value)}
                                    required
                                    className="w-full border border-gray-300 rounded px-3 py-1 focus:outline-none focus:ring focus:ring-blue-200"
                                />
                            </div>
                            <div>
                                <label className="block font-medium">Confirm New Password:</label>
                                <input
                                    type="password"
                                    value={confirmNewPassword}
                                    onChange={e => setConfirmNewPassword(e.target.value)}
                                    required
                                    className="w-full border border-gray-300 rounded px-3 py-1 focus:outline-none focus:ring focus:ring-blue-200"
                                />
                            </div>
                            <button type="submit" disabled={resetPasswordLoading} className="w-full py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition">
                                {resetPasswordLoading ? "Resetting..." : "Reset Password"}
                            </button>
                        </form>
                    </div>
                </div>
                {/* Deposits History */}
                <div className="w-full mt-5">
                    <div className="flex">
                        <h2 className="w-full w-1/2 text-2xl font-bold mb-4">Deposits History</h2>
                        <div className="w-full w-1/2 flex justify-end mb-4">
                            <button onClick={() => setIsShowDepositPopup(true)} className="px-5 py-2 text-white text-base font-semibold rounded-md bg-blue-500 hover:bg-blue-700 mr-0 sm:mr-5">
                                Deposit
                            </button>
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="table-auto min-w-[700px] w-full text-xs sm:text-sm">
                            <thead>
                                <tr className="bg-gray-300 text-center">
                                    <th className="px-4 py-2">#</th>
                                    <th className="px-4 py-2">Deposit Amount</th>
                                    <th className="px-4 py-2">Payment Method</th>
                                    <th className="px-4 py-2">Send Amount</th>
                                    <th className="px-4 py-2">Created Time</th>
                                    <th className="px-4 py-2">Reference Value</th>
                                    <th className="px-4 py-2">Status</th>
                                    <th className="px-4 py-2">Balance Before</th>
                                    <th className="px-4 py-2">Balance After</th>
                                    <th className="px-4 py-2">Verified Time</th>
                                    <th className="px-4 py-2">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dataDeposits.sort((a, b) => new Date(b.verified_at) - new Date(a.verified_at)).map((deposit) => (
                                    <tr key={deposit.id} className="text-center">
                                        <td className="border px-2 py-1 font-bold">#{deposit.id}</td>
                                        <td className="border px-2 py-1">${deposit.received_amount}</td>
                                        <td className="border px-2 py-1">{deposit?.method?.name ?? deposit?.method_callback ?? "N/A"}</td>
                                        <td className="border px-2 py-1">{deposit.trans_amount.toLocaleString()} {deposit.trans_currency}</td>
                                        <td className="border px-2 py-1">{new Date(deposit.created_at).toLocaleString()}</td>
                                        <td className="border px-2 py-1 break-all">{deposit.trans_id ?? deposit.sender_name ?? ""}</td>
                                        <td className="border px-2 py-1">
                                            <div className="flex w-full h-full justify-center items-center">

                                                {[
                                                    <CircleDot className="text-center text-yellow-500" />,
                                                    <CircleCheck className="text-center text-green-500" />,
                                                    <CircleX className="text-center text-red-500" />]
                                                [deposit.status]}
                                            </div>
                                        </td>
                                        <td className="border px-2 py-1">${deposit.before_balance?.toFixed(2) ?? "___"}</td>
                                        <td className="border px-2 py-1">${deposit.after_balance?.toFixed(2) ?? "___"}</td>
                                        <td className="border px-2 py-1">{deposit.verified_at ? new Date(deposit.verified_at).toLocaleString() : "___"}</td>
                                        <td className="border px-2 py-1">
                                            <div className="flex flex-col sm:flex-row gap-2 justify-center">
                                                {deposit.status === 0 &&
                                                    <button className="bg-blue-500 hover:bg-blue-700 rounded-md text-white font-semibold py-1 px-3" onClick={() => setDepositPaymentPopup(deposit)}>
                                                        Pay
                                                    </button>
                                                }
                                                {deposit.status === 0 &&
                                                    <button className="bg-red-500 hover:bg-red-700 rounded-md text-white font-semibold py-1 px-3" onClick={() => handleCancelDeposit(deposit)}>
                                                        Cancel
                                                    </button>
                                                }
                                                <button className="bg-green-500 hover:bg-green-700 rounded-md text-white font-semibold py-1 px-3" onClick={() => handleInvoice(deposit)}>
                                                    Invoice
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            {isShowDepositPopup &&
                <CreateDepositPopup
                    handleClose={() => setIsShowDepositPopup(false)}
                    handleCreated={(deposit) => { fetchDeposits(); setDepositPaymentPopup(deposit); setIsShowDepositPopup(false); }}
                />
            }
            {depositPaymentPopup &&
                <DepositInfoPopup
                    initDeposit={depositPaymentPopup}
                    handleClose={() => { setDepositPaymentPopup(null); fetchDeposits(); }}
                />
            }
            {invoicePopup &&
                <InvoicePopup
                    deposit={invoicePopup}
                    user={authStore.user}
                    onClose={() => setInvoicePopup(null)}
                />
            }
        </div>
    );
}

export default Profile;